import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { asDecimalString, Money, type LegIn } from "@shop-ledger/shared-types";
import { Button, Input } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createLedgerEntry, listAccounts } from "../../api/ledgerApi";
import { validateJournalLegs, type JournalLegDraft } from "../../domain/ledgerBalance";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

function emptyLeg(): JournalLegDraft {
  return { accountId: "", debit: "", credit: "" };
}

/**
 * Manual journal entry form (Accountant Control Panel, §5). Builds balanced debit/credit
 * legs client-side and validates the balance (`validateJournalLegs`,
 * `domain/ledgerBalance.ts`) before ever calling `POST /ledger/entries` — matching the
 * backend's own `LedgerEntryCreate._must_balance` check, so a user gets the same rejection
 * instantly instead of after a round trip. The DB's deferred constraint trigger remains
 * the actual enforced invariant regardless (§6.5) — this is a UX improvement, not a
 * replacement for server-side validation.
 */
export function NewJournalEntryPage() {
  const { ensureFreshToken } = useAuth();
  const navigate = useNavigate();
  const accountsState = useAsyncData(() => ensureFreshToken().then((token) => listAccounts(token)), []);

  const [branchId, setBranchId] = useState("");
  const [description, setDescription] = useState("");
  const [referenceSource, setReferenceSource] = useState("");
  const [legs, setLegs] = useState<JournalLegDraft[]>([emptyLeg(), emptyLeg()]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validation = validateJournalLegs(legs);

  function updateLeg(index: number, patch: Partial<JournalLegDraft>) {
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)));
  }

  function addLeg() {
    setLegs((prev) => [...prev, emptyLeg()]);
  }

  function removeLeg(index: number) {
    setLegs((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    if (!validation.valid) return; // the balance/validation banner below already explains why

    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const legsIn: LegIn[] = legs.map((leg) => ({
        account_id: leg.accountId,
        debit: asDecimalString(leg.debit.trim() || "0"),
        credit: asDecimalString(leg.credit.trim() || "0"),
      }));
      await createLedgerEntry(token, {
        branch_id: branchId || null,
        description: description || null,
        reference_source: referenceSource || null,
        legs: legsIn,
      });
      navigate("/ledger");
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : "Could not post journal entry.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">New Journal Entry</h1>
          <p className="page__subtitle">Legs must balance — validated here before submit, and DB-enforced regardless.</p>
        </div>
      </div>

      <form className="panel" onSubmit={handleSubmit}>
        <div className="form-grid">
          <BranchSelect
            id="je-branch"
            label="Branch"
            value={branchId}
            onChange={setBranchId}
            allOptionLabel="Enterprise-wide (no branch)"
          />
          <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          <Input
            label="Reference"
            value={referenceSource}
            onChange={(e) => setReferenceSource(e.target.value)}
            placeholder="INVOICE_ID / BILL_ID / EXPENSE_ID"
          />
        </div>

        <h2 className="panel__title">Legs</h2>
        {legs.map((leg, index) => (
          <div className="leg-row" key={index}>
            <div className="sl-field">
              <label className="sl-field__label" htmlFor={`leg-account-${index}`}>
                Account
              </label>
              <select
                id={`leg-account-${index}`}
                className="sl-input"
                value={leg.accountId}
                onChange={(e) => updateLeg(index, { accountId: e.target.value })}
              >
                <option value="">Select account…</option>
                {(accountsState.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} — {account.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Debit"
              inputMode="decimal"
              value={leg.debit}
              onChange={(e) => updateLeg(index, { debit: e.target.value, credit: e.target.value ? "" : leg.credit })}
              placeholder="0.00"
            />
            <Input
              label="Credit"
              inputMode="decimal"
              value={leg.credit}
              onChange={(e) => updateLeg(index, { credit: e.target.value, debit: e.target.value ? "" : leg.debit })}
              placeholder="0.00"
            />
            <Button type="button" variant="ghost" onClick={() => removeLeg(index)} disabled={legs.length <= 2}>
              Remove
            </Button>
          </div>
        ))}
        <div className="form-actions" style={{ marginBottom: 16 }}>
          <Button type="button" variant="secondary" onClick={addLeg}>
            + Add leg
          </Button>
          <span className="muted">
            Totals — debit: {Money.format(validation.totalDebit, TENANT_CURRENCY_FALLBACK)}, credit:{" "}
            {Money.format(validation.totalCredit, TENANT_CURRENCY_FALLBACK)}
          </span>
        </div>

        {validation.errors.length > 0 ? (
          <StatusBanner kind="warning">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {validation.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </StatusBanner>
        ) : (
          <StatusBanner kind="info">Balanced — ready to post.</StatusBanner>
        )}
        {submitError ? <StatusBanner kind="error">{submitError}</StatusBanner> : null}

        <div className="form-actions">
          <Button type="submit" disabled={!validation.valid || submitting}>
            {submitting ? "Posting…" : "Post journal entry"}
          </Button>
        </div>
      </form>
    </div>
  );
}
